import React from 'react';
import {DataGridPro, GridColDef} from '@mui/x-data-grid-pro';
import Box from '@mui/material/Box';
import type {FC} from 'react';
import {InfoOutlined} from '@mui/icons-material';
import {Typography, IconButton, Popover, PaperProps, Tooltip} from '@mui/material';
import DateRangeIcon from '@mui/icons-material/DateRange';
import dayjs from 'dayjs';
import {ReactFChildrenProps} from 'types/react/ReactFChildrenProps';

/**
 * Example data type based on your JSON structure.
 * Adjust as needed for your actual code.
 */
interface ComparedRange {
   start_date: string;
   end_date: string;
}

interface Configuration {
   start_date: string;
   end_date: string;
   compared_range: ComparedRange;
   hourlyRate: number;
   transactionsCreatedMinsSaved: number;
   transactionExecutedMinsSaved: number;
   pgpOperationMinsSaved: number;
   zipOperationMinsSaved: number;
   keyCreationOperationMinsSaved: number;
   failureNotificationMinsSave: number;
}

interface ActivityStats {
   minsSaved: number;
   variancePercent: number;
   count?: number;
}

interface Totals {
   minsSaved: number;
   variancePercent: number;
}

interface MyData {
   configuration: Configuration;
   transactionsExecuted: ActivityStats;
   transactionsCreated: ActivityStats;
   pgpOperations: ActivityStats;
   zipOperations: ActivityStats;
   keyCreationOperations: ActivityStats;
   failureNotifications: ActivityStats;
   totals: Totals;
}

/**
 * Row shape for the DataGrid
 */
interface RowData {
   id: string;
   activity: string;
   minsSaved: number;
   variancePercent: number;
   count?: number;
   popupText: string;
   period: {
      start_date: string;
      end_date: string;
   };
}

/**
 * Converts a total number of minutes into a string like "35 hours, 51 minutes".
 * If there are no hours, returns only minutes. If there are no leftover minutes, returns only hours.
 */
function convertMinsToHoursMinutes(totalMinutes: number): string {
   const hours = Math.floor(totalMinutes / 60);
   const mins = totalMinutes % 60;

   if (hours > 0 && mins > 0) {
      return `${hours} hour${hours > 1 ? 's' : ''}, ${mins} minute${mins > 1 ? 's' : ''}`;
   }
   if (hours > 0) {
      return `${hours} hour${hours > 1 ? 's' : ''}`;
   }
   return `${mins} minute${mins > 1 ? 's' : ''}`;
}

/**
 * Returns a small colored arrow and the formatted percentage, e.g.:
 *   ▲ 14.33%
 *   ▼ -5.83%
 * or null if variance is zero.
 */
function getVarianceDisplay(_variancePercent: number) {
   const variancePercent = Number(_variancePercent);
   if (variancePercent > 0) {
      return (
         <Box component="span" sx={{color: 'green', ml: 1}}>
            ▲ {variancePercent.toFixed(2)}%
         </Box>
      );
   } else if (variancePercent < 0) {
      return (
         <Box component="span" sx={{color: 'red', ml: 1}}>
            ▼ {variancePercent.toFixed(2)}%
         </Box>
      );
   }
   return null;
}
/**
 * Renders the activity name plus an info icon that shows a Popover.
 * The Popover stays open if the user moves into it (to scroll/copy).
 * It closes when the user leaves the Popover area itself.
 */
const ActivityCellWithPopup: ReactFChildrenProps<{row: RowData}> = ({row}) => {
   const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);

   // When user hovers on the icon, open the popover.
   const handleMouseEnterIcon = (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget);
   };

   // Close the popover by clearing the anchor.
   const handlePopoverClose = () => {
      setAnchorEl(null);
   };

   const open = Boolean(anchorEl);

   return (
      <Box display="flex" alignItems="center" gap={1}>
         <Typography>{row.activity}</Typography>
         <IconButton
            size="small"
            onMouseEnter={handleMouseEnterIcon}
            // We do NOT close onMouseLeave here, so user can move into the pop-up
         >
            <InfoOutlined fontSize="small" />
         </IconButton>

         {/* Popover anchored to the icon */}
         <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={handlePopoverClose}
            // Position the popover below and left-aligned with the icon
            anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
            transformOrigin={{vertical: 'top', horizontal: 'left'}}
            // Let user enter the pop-up to scroll/copy text.
            // We'll close it when the mouse leaves the pop-up area.
            PaperProps={
               {
                  onMouseLeave: handlePopoverClose,
                  sx: {
                     maxHeight: 300, // or any max height you want
                     overflowY: 'auto', // enable scrolling
                     p: 2, // padding inside pop-up
                  },
               } as PaperProps
            }
         >
            <Typography variant="body2" sx={{whiteSpace: 'pre-line'}}>
               {row.popupText}
            </Typography>
         </Popover>
      </Box>
   );
};

// Helper: returns a Tooltip that shows "Time Period" and "Count",
// or a plain span if this is the grand totals row.
function renderTimeOrMoneyCell(row: RowData, content: string, varianceDisplay: JSX.Element | null) {
   if (row.id === 'totals') {
      // No tooltip for grand totals
      return <Box component="span">{content}</Box>;
   }

   // Show a tooltip for normal rows
   return (
      <Tooltip
         title={
            <Box>
               <Typography variant="body2" gutterBottom>
                  <DateRangeIcon sx={{verticalAlign: 'bottom', marginRight: '0.3rem'}} />
                  Time Period: <b>{dayjs(row.period.start_date).format('L')}</b> through{' '}
                  <b>{dayjs(row.period.end_date).format('L')}</b>
               </Typography>
               <Typography variant="body2">
                  # Count: <b>{row.count ?? 0}</b>
               </Typography>
            </Box>
         }
         arrow
      >
         <div>
            <Box component="span">{content}</Box>
            {varianceDisplay}
         </div>
      </Tooltip>
   );
}

/**
 * A simple Data Grid to display "Activity", "Time Saved", and "Money Saved".
 */
const RoiTable: FC<{data: MyData}> = ({data}) => {
   const {hourlyRate, start_date, end_date} = data.configuration;

   const period = {start_date, end_date};
   /**
    * Build an array of row data from the JSON.
    * Each row includes: id, activity name, minsSaved, variancePercent, and (optionally) count.
    */
   const rows: RowData[] = [
      {
         id: 'totals',
         activity: 'GRAND TOTALS',
         minsSaved: data.totals.minsSaved,
         variancePercent: data.totals.variancePercent,
         popupText: `The total time and cost savings across all measurements for the specified time period.`,
         period,
      },
      {
         id: 'transactionsCreated',
         activity: 'Transactions Created',
         minsSaved: data.transactionsCreated.minsSaved,
         variancePercent: data.transactionsCreated.variancePercent,
         count: data.transactionsCreated.count,
         popupText: `Measure of how many transactions were created (or cloned) during the specified time period.`,
         period,
      },
      {
         id: 'transactionsExecuted',
         activity: 'Transactions Executed',
         minsSaved: data.transactionsExecuted.minsSaved,
         variancePercent: data.transactionsExecuted.variancePercent,
         count: data.transactionsExecuted.count,
         popupText: `Measure of job executions across the specified time period.`,
         period,
      },
      {
         id: 'pgpOperations',
         activity: 'PGP Operations Performed',
         minsSaved: data.pgpOperations.minsSaved,
         variancePercent: data.pgpOperations.variancePercent,
         count: data.pgpOperations.count,
         popupText: `Measure of how many PGP operations were performed during the specified time period.`,
         period,
      },
      {
         id: 'zipOperations',
         activity: 'Zip/Unzip Operations Performed',
         minsSaved: data.zipOperations.minsSaved,
         variancePercent: data.zipOperations.variancePercent,
         count: data.zipOperations.count,
         popupText: `Measure of how many PGP operations were performed during the specified time period.`,
         period,
      },
      {
         id: 'keyCreationOperations',
         activity: 'SSH/PGP Keys Created',
         minsSaved: data.keyCreationOperations.minsSaved,
         variancePercent: data.keyCreationOperations.variancePercent,
         count: data.keyCreationOperations.count,
         popupText: `Measure of how many SSH and PGP keys were created (or imported) during the specified time period.`,
         period,
      },
      {
         id: 'failureNotifications',
         activity: 'Failure Notifications',
         minsSaved: data.failureNotifications.minsSaved,
         variancePercent: data.failureNotifications.variancePercent,
         count: data.failureNotifications.count,
         popupText: `Measure of how many failure notifications were sent during the specified time period.`,
         period,
      },
   ];

   /**
    * Define the DataGrid columns.
    * 1) Activity: simple text.
    * 2) Time Saved: convert minsSaved to hours/mins and display variance arrow/percentage.
    * 3) Money Saved: convert minsSaved to monetary value using hourlyRate and also display variance arrow/percentage.
    */
   const columns: GridColDef[] = [
      {
         field: 'activity',
         headerName: 'Activity',
         flex: 1,
         sortable: false,
         renderCell: (params) => {
            const row = params.row as RowData;
            return <ActivityCellWithPopup row={row} />;
         },
      },
      {
         field: 'timeSaved',
         headerName: 'Time Saved',
         flex: 1,
         sortable: false,
         // We use renderCell to transform raw data into a UI (hours/min + arrow).
         renderCell: (params) => {
            const row = params.row as RowData;
            const timeString = convertMinsToHoursMinutes(row.minsSaved);
            const varianceDisplay = getVarianceDisplay(row.variancePercent);
            return renderTimeOrMoneyCell(row, timeString, varianceDisplay);
         },
      },
      {
         field: 'moneySaved',
         headerName: 'Money Saved',
         flex: 1,
         sortable: false,
         renderCell: (params) => {
            const row = params.row as RowData;
            // Convert minutes saved to hours, then multiply by hourlyRate
            const money = (row.minsSaved / 60) * hourlyRate;
            const moneyFormatted = money.toLocaleString('en-US', {
               style: 'currency',
               currency: 'USD',
            });
            const varianceDisplay = getVarianceDisplay(row.variancePercent);

            return renderTimeOrMoneyCell(row, moneyFormatted, varianceDisplay);
         },
      },
   ];

   return (
      <Box sx={{height: '100%', width: '100%'}}>
         <DataGridPro
            rows={rows}
            columns={columns}
            // Hide pagination, sorting menus, etc. for a cleaner display.
            hideFooter
            disableColumnMenu
            disableSelectionOnClick
         />
      </Box>
   );
};

export default RoiTable;
